/**
 * Centralized service for managing user preferences
 * Syncs preferences between localStorage, database, and file server
 * All three storage locations are kept in sync at all times
 */

import { storage, isStoragePersistent } from './storageService';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'https://apicabaza.creago.be/rest/cabaza') + '/peoples';

// In-memory cache of preferences
let preferencesCache = null;

// LocalStorage key for preferences
const PREFS_LOCAL_STORAGE_KEY = 'cabazaPreferences';

/**
 * Get current user ID from localStorage
 */
const getUserId = () => {
    return parseInt(storage.getItem('userId'), 10) || 0;
};

/**
 * Get all preferences from API
 * @returns {Promise<Object>} Preferences object
 */
export const getPreferencesFromApi = async () => {
    const userId = getUserId();
    if (!userId) return null;

    try {
        const response = await fetch(`${API_BASE_URL}/${userId}/preferences`, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? {};

        if (success) {
            // Normalize keys
            const prefs = {
                pinnedItems: data.pinnedItems ?? data.pinneditems ?? data.PINNEDITEMS ?? [],
                dashboardDesktops: data.dashboardDesktops ?? data.dashboarddesktops ?? data.DASHBOARDDESKTOPS ?? [],
                selectedThemeId: data.selectedThemeId ?? data.selectedthemeid ?? data.SELECTEDTHEMEID ?? 0,
                collapsedSidebarGroups: data.collapsedSidebarGroups ?? data.collapsedsidebargroups ?? data.COLLAPSEDSIDEBARGROUPS ?? {},
                language: data.language ?? data.LANGUAGE ?? 'fr'
            };
            preferencesCache = prefs;
            // Also update localStorage for sync
            savePreferencesToLocalStorage(prefs);
            return prefs;
        }

        return null;
    } catch (error) {
        console.error('Error fetching preferences from API:', error);
        return null;
    }
};

/**
 * Backup preferences before saving
 * @returns {Promise<Object>} Result with success status
 */
export const backupPreferences = async () => {
    const userId = getUserId();
    if (!userId) return { success: false, message: 'No userId' };

    try {
        const response = await fetch(`${API_BASE_URL}/${userId}/preferences/backup`, {
            method: 'POST',
            credentials: 'include'
        });

        if (!response.ok) {
            console.warn('Backup preferences warning:', response.status);
            return { success: false, message: `HTTP ${response.status}` };
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.warn('Error backing up preferences:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Save preferences to all storage locations (API/DB, file server, localStorage)
 * Saves immediately without debounce to ensure data integrity
 * @param {Object} preferences - Preferences to save (partial update supported)
 * @returns {Promise<boolean>} Success status
 */
export const savePreferencesToApi = async (preferences) => {
    const userId = getUserId();
    if (!userId) return false;

    try {
        // 1. Update in-memory cache first
        if (preferencesCache) {
            preferencesCache = { ...preferencesCache, ...preferences };
        }

        // 2. Update localStorage immediately
        const localPrefs = getPreferencesFromLocalStorage();
        const updatedPrefs = { ...localPrefs, ...preferences };
        savePreferencesToLocalStorage(updatedPrefs);

        // 3. Save to API (which saves to DB + file server)
        const response = await fetch(`${API_BASE_URL}/${userId}/preferences`, {
            method: 'PUT',
            credentials: 'include',
            body: JSON.stringify(preferences)
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        return success;
    } catch (error) {
        console.error('Error saving preferences to API:', error);
        return false;
    }
};

/**
 * Get preferences from localStorage
 */
const getPreferencesFromLocalStorage = () => {
    if (!isStoragePersistent()) return {};
    try {
        const stored = storage.getItem(PREFS_LOCAL_STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.warn('Error reading preferences from localStorage:', e);
    }
    return {};
};

/**
 * Save preferences to localStorage (only if persistent)
 */
const savePreferencesToLocalStorage = (prefs) => {
    if (!isStoragePersistent()) return;
    try {
        storage.setItem(PREFS_LOCAL_STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
        console.warn('Error saving preferences to localStorage:', e);
    }
};

/**
 * Save preferences immediately (alias for savePreferencesToApi which is now always immediate)
 * @param {Object} preferences - Preferences to save
 */
export const savePreferencesImmediately = async (preferences) => {
    return savePreferencesToApi(preferences);
};

/**
 * Get preferences from cache, localStorage, or API (in that order)
 * Syncs all storage locations if they differ
 */
export const getPreferences = async () => {
    // 1. Return from cache if available
    if (preferencesCache) {
        return preferencesCache;
    }

    // 2. If localStorage unavailable, always fetch from API (server file is source of truth)
    if (!isStoragePersistent()) {
        const apiPrefs = await getPreferencesFromApi();
        if (apiPrefs) {
            preferencesCache = apiPrefs;
            return apiPrefs;
        }
        return null;
    }

    // 3. Try localStorage first (fastest)
    const localPrefs = getPreferencesFromLocalStorage();

    // 4. Fetch from API to ensure sync
    const apiPrefs = await getPreferencesFromApi();

    if (apiPrefs) {
        // API is the source of truth - update localStorage if different
        const merged = { ...localPrefs, ...apiPrefs };
        savePreferencesToLocalStorage(merged);
        preferencesCache = merged;
        return merged;
    }

    // If API fails, return localStorage data
    if (Object.keys(localPrefs).length > 0) {
        preferencesCache = localPrefs;
        return localPrefs;
    }

    return null;
};

/**
 * Clear preferences cache (call on logout)
 */
export const clearPreferencesCache = () => {
    preferencesCache = null;
    storage.removeItem(PREFS_LOCAL_STORAGE_KEY);
};

// =====================
// Theme Preferences
// =====================

/**
 * Get selected theme ID from preferences
 */
export const getSelectedThemeId = async () => {
    const prefs = await getPreferences();
    return prefs?.selectedThemeId || 0;
};

/**
 * Save selected theme ID
 * @returns {Promise<boolean>} Success status
 */
export const saveSelectedThemeId = async (themeId) => {
    return savePreferencesToApi({ selectedThemeId: themeId });
};

// =====================
// Language Preferences
// =====================

/**
 * Get language from preferences
 */
export const getLanguagePreference = async () => {
    const prefs = await getPreferences();
    return prefs?.language || 'fr';
};

/**
 * Save language preference
 * @returns {Promise<boolean>} Success status
 */
export const saveLanguagePreference = async (language) => {
    return savePreferencesToApi({ language });
};

// =====================
// Sidebar Preferences
// =====================

/**
 * Get collapsed sidebar groups from preferences
 */
export const getCollapsedSidebarGroups = async () => {
    const prefs = await getPreferences();
    return prefs?.collapsedSidebarGroups || {};
};

/**
 * Save collapsed sidebar groups
 * @returns {Promise<boolean>} Success status
 */
export const saveCollapsedSidebarGroups = async (groups) => {
    return savePreferencesToApi({ collapsedSidebarGroups: groups });
};

// =====================
// Pinned Items
// =====================

/**
 * Get pinned items from preferences
 */
export const getPinnedItems = async () => {
    const prefs = await getPreferences();
    return prefs?.pinnedItems || [];
};

/**
 * Save pinned items
 * @returns {Promise<boolean>} Success status
 */
export const savePinnedItems = async (items) => {
    return savePreferencesToApi({ pinnedItems: items });
};
